/* tslint:disable:max-line-length */

import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CustomError } from './custom-error.service';
import { IErrorDetails, INotificationPriority, OfLoggerService, OfNotificationService } from './error-handler-interfaces.service';
import { DISPLAY_ERROR_TOKEN, LOG_ERROR_TOKEN, MESSAGE_TOKEN, REDIRECT_ADDRESS_TOKEN, STATIC_ERROR_ID_TOKEN } from './error-handler.tokens';

interface ErrorStrategy {
    /** Takes an error and passes it to the logger method the strategy was instantiated with. */
    logError: OfLoggerService['logError'];
    /** Takes an error and passes it to the notification method the strategy was instantiated with. */
    displayError: OfNotificationService['notify'];
    /** Takes an error and handles it according to the current strategy's logic. */
    execute(error: CustomError | HttpErrorResponse): void;
    /** Takes an error and passes it to the notification and logger methods the strategy was instantiated with. */
    invokeError(error: CustomError, details: IErrorDetails, message?: string | Array<any>): void;
}

// Set up the error strategy defaults...
/** A strategy to be used within the `ErrorHandlerService` for handling an error. Make use of the strategy's logic via the `execute()` method. */
@Injectable({
    providedIn: 'root'
})
abstract class BaseErrorStrategy implements ErrorStrategy {
    logError;
    displayError;
    redirectAddress;
    message?;
    staticErrorId: string;

    /** Generate a short-form support ID, */
    protected errorId = (staticErrorId?: string) => this.staticErrorId || Math.random().toString(36).substr(2, 9).toUpperCase();

    constructor (
    @Inject(DISPLAY_ERROR_TOKEN) _displayError: Function,
        @Inject(LOG_ERROR_TOKEN) _logError: Function,
        @Inject(REDIRECT_ADDRESS_TOKEN) _redirectAddress: string,
        @Inject(MESSAGE_TOKEN) _message?: string,
        @Inject(STATIC_ERROR_ID_TOKEN) _staticErrorId?: string
    ) {
        this.logError = _logError;
        this.displayError = _displayError;
        this.redirectAddress = _redirectAddress;
        this.message = _message;
        this.staticErrorId = _staticErrorId;
    }

    /** Run the strategy logic. This function will be overridden by specific strategies. */
    execute (error: CustomError | HttpErrorResponse): void {}

    /** A basic method for displaying and logging an error. */
    invokeError = (error: CustomError, details: IErrorDetails = {}, message?: string): void => {
        const receivedPriority =
			error.errorDetails && error.errorDetails.notificationPriority
			    ? error.errorDetails.notificationPriority
			    : details.notificationPriority;
        this.logError(error, details);
        this.displayError(
            message || error.message || 'No error details available',
            receivedPriority || 'minor',
            details.notificationOptions || {}
        );
    };

    /**
	 * @deprecated
	 * Create a stringified stacktrace via stacktrace-js.
	 */
    getErrorTrace = (error, convertToString = true) => {
        console.log('getErrorTrace() was called but is deprecated. The original error was returned instead of a trace.');
        return error;
    };

    /**
	 * @deprecated
	 * Stringify a stacktrace js object.
	 */
    stringifyStack (stackframes: Array<any>): string {
        console.log('stringifyStack() was called but is deprecated. There was no attempt to return a trace.');
        return 'No stack trace available.';
    }

    /** Take the message out of an error or generate a generic error message. No support ID included. */
    createHTTPErrorMessage = (error: HttpErrorResponse): string => {
        if (this.message) {
            return this.message;
        }
        if (error.status) {
            return `An HTTP error was encountered. (Status Code: ${error.status || 'Status Unavailable'}.)`;
        }
        return error.message;
    };

    /** Create a details object based on an HTTPErrorResponse with HTTP specific information populated.
	 * @arg error An Angular HttpErrorResponse object to generate an IErrorDetails object based on.
	 * @arg level The error level that will be appended to the details.
	 * @returns An IErrorDetails object that can be used when creating a CustomError.
	 */
    createHTTPErrorDetails = (error: HttpErrorResponse, level = 'error'): IErrorDetails => {
        // We were using this when this logic was in the Sentry service, but it's probably not needed here. We'll see?
        // let details;
        // if (error) {
        //   error.exception['values'] = [{type: error.message.message, value: error.message}];
        // }
        // Parse out Angular's Headers object.
        const headersContent = {};
        if (error.headers && error.headers.keys) {
            error.headers.keys().map((key) => {
                headersContent[key] = error.headers.get(key);
            });
        }
        // Create the Correlation ID tag if possible.
        const tagsContent = { label: 'HTTPError', http: 'true' };
        if (error.headers && error.headers.get && error.headers.get('x-correlation-id')) {
            tagsContent['correlation-id'] = error.headers.get('x-correlation-id');
        }

        return {
            level: level,
            fingerprint: [ error.message, error.url || error.statusText ],
            tags: tagsContent,
            showSupportId: true,
            extra: {
                Headers: { ...headersContent },
                URL: error.url || 'Unavailable',
                Status: error.status || 'Unavailable',
                Message: error.message || 'Unavailable',
                'Returned Error Object': error.error || 'None/Unavailable'
            },
            errorObject: error
        };
    };
}

// Define our strategies...
/** A strategy to be used within the `ErrorHandlerService`. For general error handling or accessing the generic strategy methods. */
@Injectable({ providedIn: 'root' })
export class GenericErrorStrategy extends BaseErrorStrategy {
    constructor (
    @Inject(DISPLAY_ERROR_TOKEN) _displayError: Function,
        @Inject(LOG_ERROR_TOKEN) _logError: Function,
        @Inject(REDIRECT_ADDRESS_TOKEN) _redirectAddress: string,
        @Inject(MESSAGE_TOKEN) _message?: string,
        @Inject(STATIC_ERROR_ID_TOKEN) _staticErrorId?: string
    ) {
        super(_displayError, _logError, _redirectAddress, _message, _staticErrorId);
    }
    execute (error: CustomError | HttpErrorResponse): void {
        // Prep any notification settings
        const err = error as CustomError;
        let priority: INotificationPriority = 'minor',
            notificationOptions = {};
        if (err.errorDetails && err.errorDetails.notificationPriority) {
            priority = err.errorDetails.notificationPriority;
        }
        if (err.errorDetails && err.errorDetails.notificationOptions) {
            notificationOptions = err.errorDetails.notificationOptions;
        }
        // Prep the message
        const sharedErrorId = this.errorId();
        const fallbackMessage = 'An unknown error occurred. Details have been sent to our technical team. If the issue persists, please contact support.';
        this.displayError(`${this.message || fallbackMessage} Support ID: ${sharedErrorId}`, priority, notificationOptions);
        this.logError(error, { tags: { 'support-id': sharedErrorId, label: 'GenericError', http: 'false' } });
    }
}

/** A strategy to be used within the `ErrorHandlerService`. For specifically handling errors that leverage the CustomError class. Currently logs and notifies with our support ID and the logger's event ID.*/
@Injectable({ providedIn: 'root' })
export class CustomErrorStrategy extends BaseErrorStrategy {
    execute (error: CustomError): void {
        const _details: IErrorDetails = error.errorDetails ? { ...error.errorDetails } : {};

        // Create and attach the support ID if requested.
        let sharedErrorId = '';
        if (_details.showSupportId === true) {
            sharedErrorId = this.errorId();
            _details.tags = {
                ...error.errorDetails.tags,
                'support-id': sharedErrorId,
                label: 'CustomError',
                http: 'false'
            };
        }

        const logResponse = this.logError(error, _details);

        // Build and display the error text.
        if (logResponse) {
            // If the logger returned an ID.
            sharedErrorId = `${logResponse}-${sharedErrorId}`;
        }
        const supportString = _details.showSupportId ? ` Support ID: ${sharedErrorId}.` : '';
        const fallbackMessage = 'An unknown error occurred. Details have been sent to our technical team. If the issue persists, please contact support.';
        const errMessage = error.message || this.message || fallbackMessage;
        this.displayError(
            errMessage + supportString,
            _details.notificationPriority || 'moderate',
            _details.notificationOptions || {
                viewOptions: {
                    text: { title: 'Error', body: errMessage + supportString },
                    controls: [ { text: 'Dismiss' } ]
                }
            }
        );
    }
}

/** A strategy to be used within the `ErrorHandlerService`. For a situation where you would like to show a notification, but avoid sending the issue to the logger. */
@Injectable({ providedIn: 'root' })
export class ShowNotificationStrategy extends BaseErrorStrategy {
    execute (error?: Error | HttpErrorResponse): void {
        const fallbackMessage =
			'An unknown error occurred. Details have been sent to our technical team. If the issue persists, please contact support.';
        this.displayError(this.message || fallbackMessage, 'critical', {
            viewOptions: {
                text: { title: 'Error', body: this.message || fallbackMessage },
                controls: [ { text: 'Dismiss' } ]
            }
        });
    }
}

/** A strategy to be used within the `ErrorHandlerService`. For handling Angular template errors. Currently displays and logs. */
@Injectable({ providedIn: 'root' })
export class TemplateErrorStrategy extends BaseErrorStrategy {
    execute (error: CustomError | HttpErrorResponse): void {
        const sharedErrorId = this.errorId();
        const fallbackMessage = 'There was an issue when attempting to display content on the page. The site may continue to work correctly, however, if not try refreshing the page. If the issue persists, please contact support.';

        this.displayError(`${this.message || fallbackMessage} Support ID: ${sharedErrorId}`, 'minor');
        this.logError(error, { tags: { 'support-id': sharedErrorId, label: 'GenericError', http: 'false' } });
    }
}

/** A strategy to be used within the `ErrorHandlerService`. For dealing situations where the user lacks authorization. Currently redirects the user to log in and does not logs as debug. */
@Injectable({ providedIn: 'root' })
export class AuthErrorStrategy extends BaseErrorStrategy {
    execute (error: HttpErrorResponse): void {
        const details = this.createHTTPErrorDetails(error, 'debug');
        // Create an actual error.
        const httpError = new CustomError(error.message, details);

        this.logError(httpError, details);
        window.location.replace(this.redirectAddress);
    }
}

/** A strategy to be used within the `ErrorHandlerService`. For handling HTTP errors. Currently logs and displays a fallback message with the status. */
@Injectable({ providedIn: 'root' })
export class HttpErrorStrategy extends BaseErrorStrategy {
    execute (error: HttpErrorResponse): void {
        const sharedErrorId = this.errorId();

        // HTTPErrorResponse isn't actually an error and loggers won't understand it, so we have to create a real error with the info in it.
        const friendlyMessage = this.createHTTPErrorMessage(error);
        const details = this.createHTTPErrorDetails(error, 'error');
        details.tags = { ...details.tags, 'support-id': sharedErrorId };

        const httpError = new CustomError(error.message, details);

        this.displayError(
            `${friendlyMessage} Support ID: ${sharedErrorId}.`,
            'moderate',
            // FIXME: Why do I have to specify a body here?
            {
                viewOptions: {
                    text: { title: 'Connection Error', body: `${friendlyMessage} Support ID: ${sharedErrorId}.` },
                    controls: [ { text: 'Dismiss' } ]
                }
            }
        );
        this.logError(httpError, details);
    }
}

/** A strategy to be used within the `ErrorHandlerService`. For handling HTTP errors. Currently logs and displays a fallback message with the status. */
@Injectable({ providedIn: 'root' })
export class HttpRefreshErrorStrategy extends BaseErrorStrategy {
    execute (error: HttpErrorResponse): void {
        const sharedErrorId = this.errorId();

        // HTTPErrorResponse isn't actually an error and loggers won't understand it, so we have to create a real error with the info in it.
        const friendlyMessage = this.createHTTPErrorMessage(error);
        const details = this.createHTTPErrorDetails(error, 'error');
        details.tags = { ...details.tags, 'support-id': sharedErrorId };

        const httpError = new CustomError(error.message, details);

        this.displayError(`${friendlyMessage} Support ID: ${sharedErrorId}.`, 'critical', {
            viewOptions: {
                text: { title: 'Please Refresh', body: `${friendlyMessage} Support ID: ${sharedErrorId}.` },
                controls: [
                    {
                        text: 'Refresh now',
                        callbackFn: () => {
                            location.reload();
                        }
                    },
                    { text: 'Don\'t refresh yet' }
                ]
            }
        });
        this.logError(httpError, details);
    }
}

/** A strategy to be used within the `ErrorHandlerService`. For handling HTTP debug messages. Currently logs and displays a fallback message with the status. */
@Injectable({ providedIn: 'root' })
export class HttpDebugStrategy extends BaseErrorStrategy {
    execute (error: HttpErrorResponse): void {
        const sharedErrorId = this.errorId();

        // HTTPErrorResponse isn't actually an error and loggers won't understand it, so we have to create a real error with the info in it.
        const friendlyMessage = this.createHTTPErrorMessage(error);
        const details = this.createHTTPErrorDetails(error, 'debug');
        details.tags = { ...details.tags, 'support-id': sharedErrorId };

        const httpError = new CustomError(error.message, details);

        this.displayError(`${friendlyMessage} Support ID: ${sharedErrorId}.`, 'moderate', {
            viewOptions: {
                text: { title: 'Error', body: `${friendlyMessage} Support ID: ${sharedErrorId}.` },
                controls: [ { text: 'Dismiss' } ]
            }
        });
        this.logError(httpError, details);
    }
}

/** A strategy to be used within the `ErrorHandlerService`. For dealing with situations where an error is thrown, but there's no internet access. Currently only displays text, since we can't connect to the remote logger. */
@Injectable({ providedIn: 'root' })
export class ConnectionErrorStrategy extends BaseErrorStrategy {
    execute (error: CustomError | HttpErrorResponse): void {
        this.displayError(
            this.message || 'There was an issue with the connection to the server. Please check your network connection.',
            'minor'
        );
    }
}
